import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Avatar,
  Box,
  Tooltip,
  Divider,
  Typography,
} from "@mui/material";

import {
  ExpandLess,
  ExpandMore,
  Home,
  Business,
  Store,
  People,
  Receipt,
  Security,
  BarChart,
  PeopleOutline,
  PeopleTwoTone,
  Diversity3,
  PinDrop,
  Source,
  AccountBalance,
  LocalShipping,
  AdminPanelSettings,
  Article,
} from "@mui/icons-material";
import { NavLink } from "react-router-dom";
import { useAuth } from "./AuthContext";
import "./Sidebar.css";

const Sidebar = ({ open }) => {
  const [masterOpen, setMasterOpen] = useState(false);
  const [transactionOpen, setTransactionOpen] = useState(false);

  const { companyName, permissions } = useAuth();

    {/* Permissions */ }
  const hasReadMasterPermission = permissions.includes('Read Master');
  const hasReadTransactionPermission = permissions.includes('Read Transaction');
  const hasReadAccountsPermission = permissions.includes('Read Accounts');
  const hasReadAdministrationPermission = permissions.includes('Read Administration');
  const hasReadReadReportsPermission = permissions.includes('Read Reports');
  const [userOpen, setUserOpen] = useState(false);


  return (
    <Drawer
      variant="permanent"
      className="modern-sidebar"
      sx={{
        width: open ? 260 : 70,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: open ? 260 : 70,
          background: "linear-gradient(180deg, #1e293b 0%, #0f172a 100%)",
          color: "white",
          overflowX: "hidden",
          transition: "width 0.3s ease",
          borderRight: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "4px 0 20px rgba(0,0,0,0.3)",
        },
      }}
    >
      {/* Header */}
      <Box className="sidebar-header" sx={{ p: open ? 3 : 2, textAlign: open ? "left" : "center" }}>
        {open ? (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "18px", color: "#fff", letterSpacing: "-0.5px" }}>
              {/* {companyName || "Dashboard"} */}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "11px" }}>
             
            </Typography>
          </Box>
        ) : (
          <Avatar sx={{ width: 40, height: 40, bgcolor: "#667eea", margin: "0 auto" }}>
            {/* {companyName ? companyName.charAt(0) : "D"} */}
          </Avatar>
        )}
      </Box>

      <Divider sx={{ bgcolor: "rgba(255,255,255,0.1)", mx: 2 }} />

      <List sx={{ mt: 2, px: 1 }}>
        {/* Home */}
        <Tooltip title={!open ? "Home" : ""} placement="right">
          <ListItemButton
            component={NavLink}
            to="/Home"
            className="sidebar-item"
            sx={{
              borderRadius: "10px",
              mb: 1,
              "&.active": {
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
              },
              "&:hover": {
                bgcolor: "rgba(102, 126, 234, 0.15)",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: open ? 40 : "auto", justifyContent: "center" }}>
              <Home sx={{ color: "white", fontSize: 22 }} />
            </ListItemIcon>
            {open && (
              <ListItemText
                primary="Home"
                primaryTypographyProps={{
                  sx: { fontWeight: 600, fontSize: "14px", fontFamily: "'Poppins', sans-serif" },
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>

        {/* Master Section */}
        {hasReadMasterPermission && (
          <>
            <ListItemButton
              onClick={() => setMasterOpen(!masterOpen)}
              className="sidebar-item"
              sx={{
                borderRadius: "10px",
                mb: 1,
                bgcolor: masterOpen ? "rgba(102, 126, 234, 0.1)" : "transparent",
                "&:hover": {
                  bgcolor: "rgba(102, 126, 234, 0.15)",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: open ? 40 : "auto", justifyContent: "center" }}>
                <Business sx={{ color: "white", fontSize: 22 }} />
              </ListItemIcon>
              {open && (
                <>
                  <ListItemText
                    primary="Master"
                    primaryTypographyProps={{
                      sx: { fontWeight: 600, fontSize: "14px", fontFamily: "'Poppins', sans-serif" },
                    }}
                  />
                  {masterOpen ? <ExpandLess /> : <ExpandMore />}
                </>
              )}
            </ListItemButton>

            <Collapse in={masterOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: open ? 2 : 0 }}>
                <ListItemButton
                  component={NavLink}
                  to="/SubContractIndex"
                  className="sidebar-subitem"
                  sx={{
                    borderRadius: "8px",
                    mb: 0.5,
                    ml: open ? 1 : 0,
                    "&.active": {
                      bgcolor: "rgba(102, 126, 234, 0.2)",
                      borderLeft: "3px solid #667eea",
                    },
                    "&:hover": {
                      bgcolor: "rgba(102, 126, 234, 0.1)",
                    },
                  }}
                >
                  {open && (
                    <ListItemText
                      primary="Subcontract Entry"
                      primaryTypographyProps={{
                        sx: { fontSize: "13px", fontFamily: "'Poppins', sans-serif", pl: 2 },
                      }}
                    />
                  )}
                </ListItemButton>
              </List>
            </Collapse>
          </>
        )}

        {/* Transaction Section */}
        {hasReadTransactionPermission && (
          <>
            <ListItemButton
              onClick={() => setTransactionOpen(!transactionOpen)}
              className="sidebar-item"
              sx={{
                borderRadius: "10px",
                mb: 1,
                bgcolor: transactionOpen ? "rgba(102, 126, 234, 0.1)" : "transparent",
                "&:hover": {
                  bgcolor: "rgba(102, 126, 234, 0.15)",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: open ? 40 : "auto", justifyContent: "center" }}>
                <Receipt sx={{ color: "white", fontSize: 22 }} />
              </ListItemIcon>
              {open && (
                <>
                  <ListItemText
                    primary="Transaction"
                    primaryTypographyProps={{
                      sx: { fontWeight: 600, fontSize: "14px", fontFamily: "'Poppins', sans-serif" },
                    }}
                  />
                  {transactionOpen ? <ExpandLess /> : <ExpandMore />}
                </>
              )}
            </ListItemButton>

            <Collapse in={transactionOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: open ? 2 : 0 }}>
                <ListItemButton
                  component={NavLink}
                  to="/PurchaseOrderIndex"
                  className="sidebar-subitem"
                  sx={{
                    borderRadius: "8px",
                    mb: 0.5,
                    ml: open ? 1 : 0,
                    "&.active": {
                      bgcolor: "rgba(102, 126, 234, 0.2)",
                      borderLeft: "3px solid #667eea",
                    },
                    "&:hover": {
                      bgcolor: "rgba(102, 126, 234, 0.1)",
                    },
                  }}
                >
                  {open && (
                    <ListItemText
                      primary="Purchase Order"
                      primaryTypographyProps={{
                        sx: { fontSize: "13px", fontFamily: "'Poppins', sans-serif", pl: 2 },
                      }}
                    />
                  )}
                </ListItemButton>

                <ListItemButton
                  component={NavLink}
                  to="/ServiceUpdateIndex"
                  className="sidebar-subitem"
                  sx={{
                    borderRadius: "8px",
                    mb: 0.5,
                    ml: open ? 1 : 0,
                    "&.active": {
                      bgcolor: "rgba(102, 126, 234, 0.2)",
                      borderLeft: "3px solid #667eea",
                    },
                    "&:hover": {
                      bgcolor: "rgba(102, 126, 234, 0.1)",
                    },
                  }}
                >
                  {open && (
                    <ListItemText
                      primary="Service Update"
                      primaryTypographyProps={{
                        sx: { fontSize: "13px", fontFamily: "'Poppins', sans-serif", pl: 2 },
                      }}
                    />
                  )}
                </ListItemButton>

                <ListItemButton
                  component={NavLink}
                  to="/StampingUpdateIndex"
                  className="sidebar-subitem"
                  sx={{
                    borderRadius: "8px",
                    mb: 0.5,
                    ml: open ? 1 : 0,
                    "&.active": {
                      bgcolor: "rgba(102, 126, 234, 0.2)",
                      borderLeft: "3px solid #667eea",
                    },
                    "&:hover": {
                      bgcolor: "rgba(102, 126, 234, 0.1)",
                    },
                  }}
                >
                  {open && (
                    <ListItemText
                      primary="Stamping Update"
                      primaryTypographyProps={{
                        sx: { fontSize: "13px", fontFamily: "'Poppins', sans-serif", pl: 2 },
                      }}
                    />
                  )}
                </ListItemButton>
              </List>
            </Collapse>
          </>
        )}

        {/* User/Auth Zen Section */}
        {hasReadAdministrationPermission && (
          <>
            <ListItemButton
              onClick={() => setUserOpen(!userOpen)}
              className="sidebar-item"
              sx={{
                borderRadius: "10px",
                mb: 1,
                bgcolor: userOpen ? "rgba(102, 126, 234, 0.1)" : "transparent",
                "&:hover": {
                  bgcolor: "rgba(102, 126, 234, 0.15)",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: open ? 40 : "auto", justifyContent: "center" }}>
                <AdminPanelSettings sx={{ color: "white", fontSize: 22 }} />
              </ListItemIcon>
              {open && (
                <>
                  <ListItemText
                    primary="Auth Zen"
                    primaryTypographyProps={{
                      sx: { fontWeight: 600, fontSize: "14px", fontFamily: "'Poppins', sans-serif" },
                    }}
                  />
                  {userOpen ? <ExpandLess /> : <ExpandMore />}
                </>
              )}
            </ListItemButton>

            <Collapse in={userOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: open ? 2 : 0 }}>
                <ListItemButton
                  component={NavLink}
                  to="/UserIndex"
                  className="sidebar-subitem"
                  sx={{
                    borderRadius: "8px",
                    mb: 0.5,
                    ml: open ? 1 : 0,
                    "&.active": {
                      bgcolor: "rgba(102, 126, 234, 0.2)",
                      borderLeft: "3px solid #667eea",
                    },
                    "&:hover": {
                      bgcolor: "rgba(102, 126, 234, 0.1)",
                    },
                  }}
                >
                  {open && (
                    <ListItemText
                      primary="Users"
                      primaryTypographyProps={{
                        sx: { fontSize: "13px", fontFamily: "'Poppins', sans-serif", pl: 2 },
                      }}
                    />
                  )}
                </ListItemButton>

                <ListItemButton
                  component={NavLink}
                  to="/RoleIndex"
                  className="sidebar-subitem"
                  sx={{
                    borderRadius: "8px",
                    mb: 0.5,
                    ml: open ? 1 : 0,
                    "&.active": {
                      bgcolor: "rgba(102, 126, 234, 0.2)",
                      borderLeft: "3px solid #667eea",
                    },
                    "&:hover": {
                      bgcolor: "rgba(102, 126, 234, 0.1)",
                    },
                  }}
                >
                  {open && (
                    <ListItemText
                      primary="Roles"
                      primaryTypographyProps={{
                        sx: { fontSize: "13px", fontFamily: "'Poppins', sans-serif", pl: 2 },
                      }}
                    />
                  )}
                </ListItemButton>

                <ListItemButton
                  component={NavLink}
                  to="/PasswordChange"
                  className="sidebar-subitem"
                  sx={{
                    borderRadius: "8px",
                    mb: 0.5,
                    ml: open ? 1 : 0,
                    "&.active": {
                      bgcolor: "rgba(102, 126, 234, 0.2)",
                      borderLeft: "3px solid #667eea",
                    },
                    "&:hover": {
                      bgcolor: "rgba(102, 126, 234, 0.1)",
                    },
                  }}
                >
                  {open && (
                    <ListItemText
                      primary="Reset Password"
                      primaryTypographyProps={{
                        sx: { fontSize: "13px", fontFamily: "'Poppins', sans-serif", pl: 2 },
                      }}
                    />
                  )}
                </ListItemButton>
              </List>
            </Collapse>
          </>
        )}

        {/* Reports Section */}
        {hasReadReadReportsPermission && (
          <Tooltip title={!open ? "Reports" : ""} placement="right">
            <ListItemButton
              component={NavLink}
              to="/CompletedJobsReport"
              className="sidebar-item"
              sx={{
                borderRadius: "10px",
                mb: 1,
                "&.active": {
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                },
                "&:hover": {
                  bgcolor: "rgba(102, 126, 234, 0.15)",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: open ? 40 : "auto", justifyContent: "center" }}>
                <Article sx={{ color: "white", fontSize: 22 }} />
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary="Completed Jobs"
                  primaryTypographyProps={{
                    sx: { fontWeight: 600, fontSize: "14px", fontFamily: "'Poppins', sans-serif" },
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        )}
      </List>

      {/* Footer */}
      {open && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            width: "100%",
            p: 2,
            textAlign: "center",
            borderTop: "1px solid rgba(255,255,255,0.1)",
            background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.2) 100%)",
          }}
        >
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", fontSize: "10px", fontWeight: 500 }}>
            © 2025 EFFTABS SOLUTIONS
          </Typography>
        </Box>
      )}
    </Drawer>
  );
};

export default Sidebar;
